import { FC, useContext, useLayoutEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Separator } from '@smartshell/ui-kit';
import cn from 'classnames';
import { createPortal } from 'react-dom';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';

import { ESessionType } from '../../types';
import { ITooltipProps } from '../interfaces';
import { getFormattedTime, getHostFunc } from '../utils';
import s from './Tooltip.module.scss';

export const Tooltip: FC<ITooltipProps> = ({
  booking,
  pageRef,
  parentRef,
  scrollContainerRef,
  isDayBooking,
  openClient,
  close,
}) => {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { t } = useContext(LocalizationContext);
  const [rootElement] = useState(document.createElement('div'));
  const apollo = useApolloClient();

  useLayoutEffect(() => {
    if (!pageRef.current) return;

    rootElement.classList.add(s.tooltipContainer);
    pageRef.current.appendChild(rootElement);

    requestAnimationFrame(() => {
      if (!parentRef.current || !scrollContainerRef.current) return;
      const {
        x,
        y,
        height: parentHeight,
        width: parentWidth,
      } = parentRef.current.getBoundingClientRect();
      const { width, height } = rootElement.getBoundingClientRect();

      const { width: viewportWidth } = window.visualViewport;

      const { x: scrollContainerX } =
        scrollContainerRef.current.getBoundingClientRect();

      const isEnoughSpaceAtTop = y - height - 4 < 0;
      const isEnoughSpaceAtRight =
        viewportWidth > x + parentWidth / 2 + width / 2;

      const isEnoughSpaceAtLeft =
        x + parentWidth / 2 - width / 2 - scrollContainerX > 0;

      if (isEnoughSpaceAtRight) {
        rootElement.style.left = `${x + parentWidth / 2 - width / 2}px`;
      } else {
        rootElement.style.left = `${viewportWidth - width - 40}px`;
      }

      if (!isEnoughSpaceAtLeft) {
        rootElement.style.left = `${scrollContainerX}px`;
      }

      if (isEnoughSpaceAtTop) {
        rootElement.style.top = `${y + parentHeight + 4}px`;
      } else {
        rootElement.style.top = `${y - height - 4}px`;
      }
    });

    return () => {
      if (!pageRef.current) return;

      pageRef.current.removeChild(rootElement);
    };
  }, [parentRef, pageRef]);

  const hostCount = booking.hostIds.length;
  const visibleHosts = booking.hostIds.slice(0, 5);

  const getHost = useMemo(() => getHostFunc(apollo), [apollo]);

  const title = useMemo(() => {
    switch (booking.type) {
      case ESessionType.ONLINE_BOOKING:
        return t('booking.online');
      case ESessionType.CLIENT_SESSION:
        return t('seance');
      case ESessionType.BOOKING:
        return !isDayBooking ? t('booking.night') : t('booking.day');
      default:
        return t('booking.hosts.count');
    }
  }, [booking]);

  return createPortal(
    <div className={s.tooltip}>
      <div className={s.header}>
        <div
          className={cn(
            s.color,
            booking.type === ESessionType.BOOKING && s.booking,
            booking.type === ESessionType.ONLINE_BOOKING && s.booking_online,
            booking.type == ESessionType.CLIENT_SESSION && s.session,
            !isDayBooking && booking.type === ESessionType.BOOKING && s.nextDay
          )}
        />
        {title}
      </div>
      <Separator />
      <div className={s.block}>
        <div className={s.row}>
          <span className={s.title}>{t('booking.start')}</span>
          <span>{getFormattedTime(booking.from)}</span>
        </div>
        <div className={s.row}>
          <span className={s.title}>{t('booking.end')}</span>
          <span>{getFormattedTime(booking.to)}</span>
        </div>
      </div>

      {booking.comment && (
        <>
          <Separator />
          <div className={s.block}>
            <div className={cn(s.row, s.comment)}>
              <span className={s.title}>{t('comment')}</span>
              <span className={s.comment__text}>{booking.comment}</span>
            </div>
          </div>
        </>
      )}
      {booking.client && (
        <>
          <Separator />
          <div className={s.block}>
            <div className={s.row}>
              <span className={s.title}>{t('client')}</span>
              <div className={s.content}>
                <span
                  className={cn(
                    s.nickname,
                    !permissions?.has('client_info') && s.nickname_disabled
                  )}
                  onClick={(event) => {
                    if (!booking.client || !permissions?.has('client_info'))
                      return;

                    event.stopPropagation();
                    close();
                    openClient(booking.client.uuid);
                  }}
                >
                  {booking.client.nickname}
                </span>
                <span className={s.phone}>{booking.client.phone}</span>
              </div>
            </div>
          </div>
        </>
      )}
      <Separator />
      <div className={s.block}>
        <div className={s.row}>
          <span className={s.title}>{t('pc')}</span>
          <div className={s.content}>
            {visibleHosts.map((hostId) => {
              const host = getHost(hostId);

              return (
                <span
                  className={s.pc_name}
                  key={`booking-${booking.id}-hosts-${hostId}`}
                >
                  {host?.alias}
                </span>
              );
            })}
            {hostCount > visibleHosts.length && (
              <span className={s.moreHosts}>
                {t('booking.hosts.count.more', hostCount - visibleHosts.length)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>,
    rootElement
  );
};
